import { useCallback, useEffect, useState } from "react";
import { IoCloseOutline, IoSearchOutline } from "react-icons/io5";
import { toast } from "react-toastify";
import apis from "services";

const SupportWhitelistPanel = ({ open, onClose }) => {
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState("");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 0 });

  const loadMembers = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const response = await apis.getSupportAccessList({ page, limit: 20 });
      const data = response?.data?.data;
      setMembers(data?.users || []);
      setPagination({ total: data?.total || 0, totalPages: data?.totalPages || 0 });
    } catch (error) {
      toast.error(error.message || "Could not load the support whitelist");
    } finally {
      setLoading(false);
    }
  }, [open, page]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [onClose, open]);

  useEffect(() => {
    if (!open) return undefined;
    const query = search.trim();
    if (!query) {
      setResults([]);
      setSearching(false);
      return undefined;
    }
    const timeout = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await apis.searchSupportAccessCandidates({ search: query, page: 1, limit: 20 });
        setResults(response?.data?.data?.users || []);
      } catch (error) {
        setResults([]);
        toast.error(error.message || "Could not search investors");
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(timeout);
  }, [open, search]);

  const grant = async (user) => {
    setBusyId(user._id);
    try {
      await apis.grantSupportAccess({ userId: user._id });
      setResults((current) => current.map((item) => item._id === user._id
        ? { ...item, supportAccess: true }
        : item));
      await loadMembers();
      toast.success(`${user.name || user.email} can now answer support conversations`);
    } catch (error) {
      toast.error(error.message || "Could not grant support access");
    } finally {
      setBusyId("");
    }
  };

  const revoke = async (user) => {
    if (!window.confirm(`Remove support access from ${user.name || user.email}?`)) return;
    setBusyId(user._id);
    try {
      await apis.revokeSupportAccess(user._id);
      setMembers((current) => current.filter((item) => item._id !== user._id));
      setResults((current) => current.map((item) => item._id === user._id
        ? { ...item, supportAccess: false }
        : item));
      setPagination((current) => ({ ...current, total: Math.max(0, current.total - 1) }));
      toast.success("Support access removed");
    } catch (error) {
      toast.error(error.message || "Could not remove support access");
    } finally {
      setBusyId("");
    }
  };

  if (!open) return null;

  return (
    <div className="support-access-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <section className="support-access-dialog" role="dialog" aria-modal="true" aria-labelledby="support-access-title">
        <header className="support-access-dialog-header">
          <div>
            <span className="support-access-eyebrow">Whitelist access</span>
            <h3 id="support-access-title">Support agents</h3>
            <p>
              Whitelisted support agents can reply on behalf of the admin, change conversation status,
              resolve or reopen conversations, and share files or reference details.
            </p>
          </div>
          <div className="support-access-header-actions">
            <strong>{pagination.total} active</strong>
            <button type="button" aria-label="Close support agents" onClick={onClose}><IoCloseOutline /></button>
          </div>
        </header>

        <div className="support-access-dialog-body">
          <label className="support-whitelist-search">
            <IoSearchOutline />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by email, user ID, or wallet ID"
              autoComplete="off"
              autoFocus
            />
            {searching && <span>Searching…</span>}
          </label>

          <div className="support-access-columns">
            <div className="support-access-column">
              <div className="support-access-section-title">
                <h4>Search results</h4>
                <span>Add an investor</span>
              </div>
              <div className="support-access-list">
                {!search.trim() ? (
                  <div className="support-access-empty compact">
                    <IoSearchOutline />
                    <p>Enter an email, user ID, or wallet ID.</p>
                  </div>
                ) : results.length === 0 && !searching ? (
                  <div className="support-access-empty"><p>No matching investors found.</p></div>
                ) : results.map((user) => (
                  <div className="support-whitelist-user" key={user._id}>
                    <span className="support-access-avatar">{(user.name || user.email || "I").charAt(0).toUpperCase()}</span>
                    <div>
                      <strong>{user.name || "Investor"}</strong>
                      <span>{user.email}</span>
                    </div>
                    <button type="button" disabled={busyId === user._id || user.supportAccess} onClick={() => grant(user)}>
                      {user.supportAccess ? "Added" : busyId === user._id ? "Adding…" : "Add"}
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="support-access-column active-agents">
              <div className="support-access-section-title">
                <h4>Active agents</h4>
                <span>Can answer all tickets</span>
              </div>
              <div className="support-access-list">
                {loading ? <div className="loader" /> : members.length === 0 ? (
                  <div className="support-access-empty"><p>No whitelisted support agents yet.</p></div>
                ) : members.map((user) => (
                  <div className="support-whitelist-user" key={user._id}>
                    <span className="support-access-avatar active">{(user.name || user.email || "I").charAt(0).toUpperCase()}</span>
                    <div>
                      <strong>{user.name || "Investor"}</strong>
                      <span>{user.email}</span>
                    </div>
                    <button type="button" className="remove" disabled={busyId === user._id} onClick={() => revoke(user)}>
                      {busyId === user._id ? "Removing…" : "Remove"}
                    </button>
                  </div>
                ))}
              </div>

              {pagination.totalPages > 1 && (
                <div className="support-whitelist-pagination">
                  <button type="button" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>Previous</button>
                  <span>{page} / {pagination.totalPages}</span>
                  <button type="button" disabled={page >= pagination.totalPages} onClick={() => setPage((value) => value + 1)}>Next</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SupportWhitelistPanel;
